import { createFreshProgress, saveProgress } from '../shared/progress';
import { generateFamilyCode, isValidFamilyCode } from '../shared/sync-client';
import type { TextbookPublisher } from '../shared/types';

export function runOnboarding(container: HTMLElement, onComplete: () => void): void {
  const root = document.createElement('div');
  root.className = 'onboarding';
  container.appendChild(root);

  // 2-step flow: step 0 = name, step 1 = textbook + family-code combined
  let step = 0;
  let studentName = '';
  let mathBook: TextbookPublisher = '康軒';
  let chineseBook: TextbookPublisher = '康軒';
  let englishBook: TextbookPublisher = '翰林';
  let natureBook: TextbookPublisher = '南一';
  let socialBook: TextbookPublisher = '翰林';
  let familyCode: string | undefined;

  renderStep();

  function renderStep(): void {
    root.innerHTML = '';
    if (step === 0) renderName();
    else renderTextbookAndCode();
  }

  function renderName(): void {
    root.innerHTML = `
      <h1>歡迎來到永恆要塞</h1>
      <p>請輸入你的暱稱(也可以跳過,預設為「指揮官」)</p>
      <input class="text-input" id="name-input" type="text" placeholder="指揮官" maxlength="12" />
      <div style="display: flex; gap: 12px;">
        <button class="secondary-btn" id="skip-name">我是指揮官</button>
        <button class="primary-btn" id="next-name">下一步</button>
      </div>
    `;
    const input = root.querySelector('#name-input') as HTMLInputElement;
    input.focus();
    root.querySelector('#skip-name')!.addEventListener('click', () => {
      studentName = '指揮官';
      step = 1; renderStep();
    });
    root.querySelector('#next-name')!.addEventListener('click', () => {
      studentName = input.value.trim() || '指揮官';
      step = 1; renderStep();
    });
  }

  /** Collapsed step 2: textbook version + family code on one screen */
  function renderTextbookAndCode(): void {
    const generated = generateFamilyCode();
    root.innerHTML = `
      <h1>快速設定 (可稍後改)</h1>
      <p style="max-width: 480px; text-align: center; color: #80cbc4; font-size: 14px;">
        選擇你的課本出版社 — <strong>之後可以在設定裡更改</strong>。
      </p>
      <div style="display: flex; flex-direction: column; gap: 12px;">
        ${publisherRow('數學', 'math', mathBook)}
        ${publisherRow('國語', 'chinese', chineseBook)}
        ${publisherRow('英語', 'english', englishBook)}
        ${publisherRow('自然', 'nature', natureBook)}
        ${publisherRow('社會', 'social', socialBook)}
      </div>
      <details style="margin-top: 12px; max-width: 480px;">
        <summary style="cursor: pointer; color: #80cbc4; font-size: 13px;">家庭碼設定 (選用 — 換裝置不怕進度消失)</summary>
        <div style="padding: 12px 0; display: flex; flex-direction: column; gap: 8px;">
          <input class="text-input" id="family-code-input" type="text"
            placeholder="輸入已有家庭碼,或自動產生"
            maxlength="6" style="text-transform: uppercase; letter-spacing: 4px; font-size: 1.2em; width: 200px;" />
          <button class="secondary-btn" id="gen-code" style="width: fit-content;">自動產生家庭碼</button>
          <span id="family-code-error" style="color: #ef9a9a; min-height: 1.2em; font-size: 12px;"></span>
        </div>
      </details>
      <div style="display: flex; gap: 12px; margin-top: 16px; flex-wrap: wrap;">
        <button class="secondary-btn" id="back-step2">← 上一步</button>
        <button class="primary-btn" id="skip-setup">跳過 / 用預設</button>
        <button class="primary-btn" id="finish-onboard" style="background: #4caf50;">完成設定</button>
      </div>
    `;

    bindPublisherRow('math',    v => { mathBook    = v; });
    bindPublisherRow('chinese', v => { chineseBook = v; });
    bindPublisherRow('english', v => { englishBook = v; });
    bindPublisherRow('nature',  v => { natureBook  = v; });
    bindPublisherRow('social',  v => { socialBook  = v; });

    const input = root.querySelector('#family-code-input') as HTMLInputElement;
    const errEl = root.querySelector('#family-code-error') as HTMLElement;

    input.addEventListener('input', () => {
      const pos = input.selectionStart ?? input.value.length;
      input.value = input.value.toUpperCase();
      input.setSelectionRange(pos, pos);
      errEl.textContent = '';
    });

    root.querySelector('#gen-code')!.addEventListener('click', () => {
      input.value = generated;
      errEl.textContent = '';
    });

    root.querySelector('#back-step2')!.addEventListener('click', () => {
      step = 0; renderStep();
    });

    root.querySelector('#skip-setup')!.addEventListener('click', () => {
      familyCode = undefined;
      finishOnboarding();
    });

    root.querySelector('#finish-onboard')!.addEventListener('click', () => {
      const val = input.value.trim().toUpperCase();
      if (val !== '' && !isValidFamilyCode(val)) {
        errEl.textContent = '家庭碼必須是 6 個大寫英數字(A-Z, 2-9)';
        return;
      }
      familyCode = val || undefined;
      finishOnboarding();
    });
  }

  function finishOnboarding(): void {
    const progress = createFreshProgress(studentName);
    progress.textbookPrefs.math     = mathBook;
    progress.textbookPrefs.chinese  = chineseBook;
    progress.textbookPrefs.english  = englishBook;
    progress.textbookPrefs.nature   = natureBook;
    progress.textbookPrefs.social   = socialBook;
    if (familyCode) progress.familyCode = familyCode;
    saveProgress(progress);
    root.remove();
    onComplete();
  }

  function publisherRow(label: string, key: string, current: TextbookPublisher): string {
    const opts: TextbookPublisher[] = ['南一', '康軒', '翰林'];
    return `
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="width: 60px;">${label}</span>
        ${opts.map(o => `
          <button class="secondary-btn pub-${key}" data-val="${o}"
            style="${o === current ? 'background:#ffd54f; color:#1a2730;' : ''}">${o}</button>
        `).join('')}
      </div>
    `;
  }

  function bindPublisherRow(key: string, set: (v: TextbookPublisher) => void): void {
    root.querySelectorAll(`.pub-${key}`).forEach(btn => {
      btn.addEventListener('click', () => {
        set((btn as HTMLElement).dataset.val as TextbookPublisher);
        renderStep();
      });
    });
  }
}
